import { useParams } from '@/router'
import { Loader } from '@/components/ui'
import { useQuery } from '@tanstack/react-query'
import { StateMachine } from 'xstate'
import { useMachine } from '@xstate/react'
import { useCheckoutServiceStore } from '@/utils/hooks/stores/checkout'
import { Suspense, lazy, useEffect } from 'react'

const Default = lazy(() => import('@/themes/payment/[id]/default'))
const Custom = lazy(() => import('@/themes/payment/[id]/custom'))
const United = lazy(() => import('@/themes/payment/[id]/united'))
const UnitedWayGreaterToronto = lazy(() => import('@/themes/payment/[id]/United_Way_Greater_Toronto'))
const CPCPastaDinner = lazy(() => import('@/themes/payment/[id]/CPC_Pasta_Dinner'))
const Interac = lazy(() => import('@/themes/payment/[id]/interac'))
const InteracFundraising = lazy(
  () => import('@/themes/payment/[id]/interac_fundraising'),
)
const Pickering = lazy(() => import('@/themes/payment/[id]/pickering'))
const ETF = lazy(() => import('@/themes/payment/[id]/etf'))
const NiagaraSouth = lazy(() => import('@/themes/payment/[id]/niagara_south'))
const ElmwoodTransconaJetsGameNight = lazy(
  () => import('@/themes/payment/[id]/elmwood_transcona_jets_game_night'),
)
const ElmwoodTranscona = lazy(
  () => import('@/themes/payment/[id]/elmwood_transcona'),
)
const MultiTicketDonation = lazy(
  () => import('@/themes/payment/[id]/multi-ticket_donation'),
)
const CPCSingleCustom = lazy(
  () => import('@/themes/payment/[id]/cpc_single_custom'),
)
const Mississaugalakeshore = lazy(
  () => import('@/themes/payment/[id]/mississaugalakeshore'),
)
const Fundraiser = lazy(
  () => import('@/themes/payment/[id]/cpc_fundraiser_event'),
)
const JerseyNight2025 = lazy(
  () => import('@/themes/payment/[id]/jersey_night_2025'),
)
const JerseyNight2025_2 = lazy(
  () => import('@/themes/payment/[id]/jersey_night_2025_2'),
)
const MultiTicketDonation2 = lazy(
  () => import('@/themes/payment/[id]/multi-ticket_donation_2'),
)
const UniversityRosedale = lazy(
  () => import('@/themes/payment/[id]/university_rosedale'),
)
const Waterloo = lazy(() => import('@/themes/payment/[id]/waterloo'))
const KidsHelpPhone = lazy(() => import('@/themes/payment/[id]/Kids_Help_Phone'))
const ScottAitchisonEvent = lazy(
  () => import('@/themes/payment/[id]/Scott_Aitchison_Event'),
)

const PageComponent: React.FC = () => {
  const params = useParams('/:language/payment/:id')

  const { isLoading, isError, isSuccess, data, error } = useQuery<any, any>({
    queryKey: ['checkout'],
    queryFn: async () => {
      let response

      try {
        response = await fetch(
          `/api/payment/session/${params.id}/process/start`,
          {
            method: 'GET',
          },
        ).then((response) => response.json())
      } catch (error) {
        console.error(error)

        return Promise.reject({
          title: 'Something went wrong!',
          detail: 'Please try again later.',
        })
      }

      if ('errors' in response) {
        return Promise.reject({
          title: response.errors?.[0]?.title,
          detail: response.errors?.[0]?.detail,
        })
      }

      console.log('loading machine')

      // Choose the checkout machine
      const machine = await (async (theme: string) => {
        switch (theme) {
          case 'default': {
            const { checkoutMachine } = await import(
              '@/utils/hooks/machines/checkout_machine'
            )
            console.log('choosing checkout')
            return checkoutMachine
          }
          case 'custom': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing donation')
            return donationMachine
          }
          case 'united': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing united')
            return donationMachine
          }
          case 'United_Way_Greater_Toronto': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing United_Way_Greater_Toronto')
            return donationMachine
          }
          case 'interac': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/interac_donation_machine'
            )
            console.log('choosing interac')
            return donationMachine
          }
          case 'interac_fundraising': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/interac_fundraising_donation_machine'
            )
            console.log('choosing interac_fundraising')
            return donationMachine
          }
          case 'niagara_south': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing niagara_south')
            return donationMachine
          }
          case 'mississaugalakeshore': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/mississaugalakeshore_machine'
            )
            console.log('choosing mississaugalakeshore')
            return donationMachine
          }
          case 'pickering': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing pickering')
            return donationMachine
          }
          case 'eTransfer only': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing eTransfer only')
            return donationMachine
          }
          case 'elmwood-transcona-Jets-Game-Night': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing donation')
            return donationMachine
          }
          case 'multi-ticket_donation': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing multi-ticket_donation')
            return donationMachine
          }
          case 'cpc_single_custom': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing cpc_single_custom')
            return donationMachine
          }
          case 'cpc_fundraiser_event': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing cpc_fundraiser_event')
            return donationMachine
          }
          case 'jersey_night_2025': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing jersey_night_2025')
            return donationMachine
          }
          case 'jersey_night_2025_2': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing jersey_night_2025_2')
            return donationMachine
          }
          case 'multi-ticket_donation_2': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing multi-ticket_donation_2')
            return donationMachine
          }
          case 'university_rosedale': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing university_rosedale')
            return donationMachine
          }
          case 'elmwood_transcona': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing elmwood_transcona')
            return donationMachine
          }
          case 'waterloo': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing waterloo')
            return donationMachine
          }
          case 'Kids_Help_Phone': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/Kids_Help_Phone_donation_machine'
            )
            console.log('choosing Kids_Help_Phone')
            return donationMachine
          }
          case 'Scott_Aitchison_Event': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing Scott_Aitchison_Event')
            return donationMachine
          }
          case 'CPC_Pasta_Dinner': {
            const { donationMachine } = await import(
              '@/utils/hooks/machines/donation_machine'
            )
            console.log('choosing CPC_Pasta_Dinner')
            return donationMachine
          }
        }
      })(response.data.attributes.config.occ.theme.name)

      if (!machine) {
        return Promise.reject({
          title: 'Something went wrong!',
          detail: 'Please try again later.',
        })
      }

      return Promise.resolve({
        response,
        machine,
      })
    },
  })

  if (isLoading) {
    return <Loader />
  }

  if (isError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        {/* HACK: set a default for this */}
        <h3>{error.title}</h3>
        <p>{error.detail}</p>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <CheckoutServiceLoader
        id={params.id}
        machine={data.machine}
        data={data.response}
      />
    )
  }
}

type CheckoutServiceLoaderProps = {
  id: string
  machine: StateMachine<any, any, any>
  data: any
}

export const CheckoutServiceLoader: React.FC<CheckoutServiceLoaderProps> = ({
  id,
  machine,
  data,
}) => {
  const attachCheckoutService = useCheckoutServiceStore(
    (state) => state.attachCheckoutService,
  )

  // Create the machine
  const checkoutService = useMachine(machine, {
    context: {
      session: {
        uid: id,
      },
    },
  })

  attachCheckoutService(checkoutService)

  const [state, send] = checkoutService
  const currentState = state.toStrings()[state.toStrings().length - 1]

  useEffect(() => {
    if (/bootstrapping.idle/.test(currentState)) {
      send('LOAD', {
        data,
      })
    }
  }, [state.value])

  if (/bootstrapping.idle/.test(currentState)) {
    return <div></div>
  }

  const theme = data.data.attributes.config.occ.theme.name

  console.log({ theme })

  return (
    <>
      {theme === 'default' && (
        <Suspense fallback={<Loader />}>
          <Default />
        </Suspense>
      )}
      {theme === 'custom' && (
        <Suspense fallback={<Loader />}>
          <Custom />
        </Suspense>
      )}
      {theme === 'united' && (
        <Suspense fallback={<Loader />}>
          <United />
        </Suspense>
      )}
      {theme === 'United_Way_Greater_Toronto' && (
        <Suspense fallback={<Loader />}>
          <UnitedWayGreaterToronto />
        </Suspense>
      )}
      {theme === 'interac' && (
        <Suspense fallback={<Loader />}>
          <Interac />
        </Suspense>
      )}
      {theme === 'interac_fundraising' && (
        <Suspense fallback={<Loader />}>
          <InteracFundraising />
        </Suspense>
      )}
      {theme === 'cpc_fundraiser_event' && (
        <Suspense fallback={<Loader />}>
          <Fundraiser />
        </Suspense>
      )}
      {theme === 'niagara_south' && (
        <Suspense fallback={<Loader />}>
          <NiagaraSouth />
        </Suspense>
      )}
      {theme === 'mississaugalakeshore' && (
        <Suspense fallback={<Loader />}>
          <Mississaugalakeshore />
        </Suspense>
      )}
      {theme === 'pickering' && (
        <Suspense fallback={<Loader />}>
          <Pickering />
        </Suspense>
      )}
      {theme === 'eTransfer only' && (
        <Suspense fallback={<Loader />}>
          <ETF />
        </Suspense>
      )}
      {theme === 'elmwood-transcona-Jets-Game-Night' && (
        <Suspense fallback={<Loader />}>
          <ElmwoodTransconaJetsGameNight />
        </Suspense>
      )}
      {theme === 'multi-ticket_donation' && (
        <Suspense fallback={<Loader />}>
          <MultiTicketDonation />
        </Suspense>
      )}
      {theme === 'cpc_single_custom' && (
        <Suspense fallback={<Loader />}>
          <CPCSingleCustom />
        </Suspense>
      )}
      {theme === 'jersey_night_2025' && (
        <Suspense fallback={<Loader />}>
          <JerseyNight2025 />
        </Suspense>
      )}
      {theme === 'jersey_night_2025_2' && (
        <Suspense fallback={<Loader />}>
          <JerseyNight2025_2 />
        </Suspense>
      )}
      {theme === 'multi-ticket_donation_2' && (
        <Suspense fallback={<Loader />}>
          <MultiTicketDonation2 />
        </Suspense>
      )}
      {theme === 'university_rosedale' && (
        <Suspense fallback={<Loader />}>
          <UniversityRosedale />
        </Suspense>
      )}
      {theme === 'elmwood_transcona' && (
        <Suspense fallback={<Loader />}>
          <ElmwoodTranscona />
        </Suspense>
      )}
      {theme === 'waterloo' && (
        <Suspense fallback={<Loader />}>
          <Waterloo />
        </Suspense>
      )}
      {theme === 'Kids_Help_Phone' && (
        <Suspense fallback={<Loader />}>
          <KidsHelpPhone />
        </Suspense>
      )}
      {theme === 'Scott_Aitchison_Event' && (
        <Suspense fallback={<Loader />}>
          <ScottAitchisonEvent />
        </Suspense>
      )}
      {theme === 'CPC_Pasta_Dinner' && (
        <Suspense fallback={<Loader />}>
          <CPCPastaDinner />
        </Suspense>
      )}
    </>
  )
}

export default PageComponent
